import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import ReactTable from "react-table";
import moment from "moment";
import _ from "lodash";

import { draftStatusOptions } from "utils/constant";
import ReactTableFilterSelect from "components/custom/forms/react-table-filter-select";
import filterCaseInsensitive from "components/custom/forms/react-table-case-insensitive";
import CustomCheckbox from "components/custom/forms/custom-checkbox";
import UserTypes from "components/auth/user-types";

class DraftTable extends React.Component {
    constructor() {
        super();
        this.statusFilterValues = {
            "": "Show All"
        };

        this.state = {
            currentDataIdArr: []
        };

        this.getColumns = this.getColumns.bind(this);
        this.setCurrentDataIdArr = this.setCurrentDataIdArr.bind(this);

        this.reactTable = React.createRef();

        draftStatusOptions.forEach(item => {
            this.statusFilterValues[item.value] = item.label;
        });
    }

    shouldComponentUpdate(nextProps, nextState) {
        if (nextProps.loading !== this.props.loading) {
            return true;
        } else if (nextProps.selectAll !== this.props.selectAll) {
            return true;
        } else if (!_.isEqual(nextProps.selected, this.props.selected)) {
            return true;
        } else if (!_.isEqual(nextProps.drafts, this.props.drafts)) {
            return true;
        } else if (!_.isEqual(nextState.currentDataIdArr, this.state.currentDataIdArr)) {
            return true;
        }
        return false;
    }

    componentWillReceiveProps(nextProps) {
        if (!_.isEqual(nextProps.drafts, this.props.drafts)) {
            this.setCurrentDataIdArr(nextProps.drafts);
        }
    }

    getColumns = () => {
        const isSupervisor = this.props.auth.user.role.includes(UserTypes.supervisor);
        const classNameFlexCenter =
            "d-flex justify-content-center align-items-center";

        const columns = [];

        columns.push({
            id: "checkbox",
            accessor: "",
            Cell: ({ original }) => (
                <CustomCheckbox
                    key={original.id}
                    className="checkbox"
                    checked={this.props.selected[original.id] === true}
                    onChange={() => this.props.toggleRow(original.id, original)}
                />
            ),
            Header: () => (
                <input
                    type="checkbox"
                    className="checkbox"
                    checked={this.props.selectAll === 1}
                    ref={input => {
                        if (input) {
                            input.indeterminate = this.props.selectAll === 2;
                        }
                    }}
                    onChange={() => this.props.toggleSelectAll(this.state.currentDataIdArr)}
                />
            ),
            sortable: false,
            filterable: false,
            resizable: false,
            minResizeWidth: 50,
            width: 50,
            className: classNameFlexCenter
        });
        columns.push({
            Header: "Name",
            accessor: "name",
            id: "name",
            minResizeWidth: 50,
            Cell: ({ original }) => {
                if (isSupervisor) {
                    return <Link to={`/schedule/drafts/details/${original.id}`}>{original.name}</Link>;
                }
                return <Link to={`/schedule/drafts/${original.id}`}>{original.name}</Link>;
            }
        });
        columns.push({
            Header: "Retailer",
            accessor: "retailer",
            minResizeWidth: 50
        });
        columns.push({
            Header: "Services count",
            accessor: "detailsCount",
            minResizeWidth: 50,
            width: 100,
            className: classNameFlexCenter
        });
        columns.push({
            Header: "Stores count",
            accessor: "storesCount",
            minResizeWidth: 50,
            width: 100,
            className: classNameFlexCenter
        });
        columns.push({
            Header: "Jobs count",
            accessor: "jobsCount",
            minResizeWidth: 50,
            width: 100,
            className: classNameFlexCenter
        });
        columns.push({
            Header: "Created",
            id: "createOn",
            accessor: original =>
                `${moment(original.createdOn).format("YYYY-MM-DD")} ${
                    original.createdBy
                    }`,
            Cell: ({ original }) => {
                const date = moment(original.createdOn).format("MMMM DD, YYYY");
                return <span>{`${date} - ${original.createdBy}`}</span>;
            },
            filterable: true,
            minResizeWidth: 50
        });
        columns.push({
            Header: "Last Changed",
            id: "modifiedOn",
            accessor: original =>
                `${moment(original.modifiedOn).format("YYYY-MM-DD")} ${
                    original.modifiedBy
                    }`,
            Cell: ({ original }) => {
                if (original.modifiedOn && original.modifiedBy) {
                    const date = moment(original.modifiedOn).format("MMMM DD, YYYY");
                    return <span>{`${date} - ${original.modifiedBy}`}</span>;
                }
                return "";
            },
            filterable: true,
            minResizeWidth: 50
        });

        if (!isSupervisor) {
            columns.push({
                Header: "Status",
                id: "status",
                accessor: "status",
                minResizeWidth: 50,
                className: classNameFlexCenter,
                Cell: ({ original }) => {
                    switch (original.status) {
                        case "InProgress":
                            return "In Progress";
                        case "ReadyForSupervisor":
                            return "Ready For Supervisor";
                        default:
                            return original.status;
                    }
                },
                Filter: ({ filter, onChange }) => (
                    <ReactTableFilterSelect
                        currentValue={filter ? filter.value : ""}
                        onChange={value => onChange(value)}
                        style={{ width: "100%", height: "100%" }}
                        optionsObject={this.statusFilterValues}
                    />
                )
            });
            columns.push({
                Header: "Active?",
                id: "isActive",
                accessor: original => (original.isInactive ? "NO" : "YES"),
                Cell: ({ original }) => (original.isInactive ? "NO" : "YES"),
                minResizeWidth: 50,
                className: classNameFlexCenter,
                Filter: ({ filter, onChange }) => (
                    <ReactTableFilterSelect
                        currentValue={filter ? filter.value : ""}
                        onChange={value => onChange(value)}
                        style={{ width: "100%", height: "100%" }}
                        optionsObject={{
                            "": "Show All",
                            YES: "Active",
                            NO: "Inactive"
                        }}
                    />
                )
            });
        }

        return columns;
    };

    onTableViewChange = () => {
        const current = this.reactTable.current;
        if (current) {
            const currentData = current
                .getResolvedState()
                .sortedData.map(item => item._original);
            this.setCurrentDataIdArr(currentData);
        }
    };

    setCurrentDataIdArr(drafts) {
        const currentDataIdArr = drafts.map((value) => parseInt(value.id, 10));
        this.setState({ currentDataIdArr });
    }

    render() {
        const {
          loading,
          drafts
        } = this.props;

        return (
            <ReactTable
                data={drafts}
                headerClassName="table-header"
                columns={this.getColumns()}
                defaultPageSize={10}
                pageSize={drafts ? drafts.length : 10}
                showPagination={false}
                pageSizeOptions={[10, 20, 50, 100]}
                resizable
                className="-striped -highlight"
                loading={loading}
                filterable
                defaultFiltered={[
                    {
                        id: "isActive",
                        value: "YES"
                    }
                ]}
                defaultSorted={[
                    {
                        id: "createOn",
                        desc: true
                    }
                ]}
                defaultFilterMethod={filterCaseInsensitive}
                style={{
                    maxHeight: "calc(100vh - 200px)",
                    height: loading ? "calc(100vh - 200px)" : "auto"
                }}
                onFilteredChange={this.onTableViewChange}
                ref={this.reactTable}
            />
        );
    }
}

DraftTable.propTypes = {
    loading: PropTypes.bool.isRequired
};

const mapStateToProps = state => ({
    loading: state.draft.loading,
    auth: state.auth,
});

export default connect(mapStateToProps)(DraftTable);
